import React from "react";
import {
  AbsoluteFill,
  Audio,
  interpolate,
  staticFile,
  useCurrentFrame,
} from "remotion";
import {
  ShieldCheck,
  CheckCircle2,
  Lock,
  QrCode,
  Sparkles,
} from "lucide-react";

export const KyuStoreWebDarkTitanium: React.FC = () => {
  const frame = useCurrentFrame();

  // Color Tokens: Dark Titanium & Neon Cyan/Emerald (High-end Fintech)
  const bgTitanium = "#0B0D14";
  const surfaceGlass = "rgba(22, 27, 46, 0.8)";
  const borderTitanium = "rgba(59, 130, 246, 0.3)";
  const cyanGlow = "#38BDF8";
  const emeraldGlow = "#34D399";
  const purpleGlow = "#A78BFA";

  // Scanner Laser Animation
  const laserY = interpolate((frame * 4) % 180, [0, 180], [0, 100]);

  // Timed Subtitles (31s = ~930 frames)
  const SUBTITLES = [
    { from: 0, to: 120, text: "Masih jaman top-up game nunggu admin bales berjam-jam?", highlight: "nunggu admin" },
    { from: 120, to: 270, text: "Udah nunggu lama... salah ketik ID pula. Nih, web baru Kyu Store!", highlight: "web baru Kyu Store" },
    { from: 270, to: 480, text: "Tampilan baru jauh lebih kenceng, cukup ketik User ID langsung kedeteksi!", highlight: "langsung kedeteksi" },
    { from: 480, to: 690, text: "Bayar tinggal scan QRIS, 0.8 detik saldo langsung mendarat otomatis!", highlight: "0.8 detik" },
    { from: 690, to: 935, text: "Gak pake ribet login, gak pake password. Cobain di kyustore.com!", highlight: "kyustore.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgTitanium,
        color: "#F8FAFC",
        fontFamily: "'Rajdhani', -apple-system, sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. Deep Space Radial Glows */}
      <div
        style={{
          position: "absolute",
          top: -100,
          left: -100,
          width: 800,
          height: 800,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(56, 189, 248, 0.18) 0%, transparent 70%)",
          filter: "blur(100px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "40%",
          right: -150,
          width: 750,
          height: 750,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(167, 139, 250, 0.16) 0%, transparent 65%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* 2. Top Header Zone (Safe Zone Y: 260 - 380 px) */}
      <div
        style={{
          position: "absolute",
          top: 270,
          left: 60,
          right: 60,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 24px",
            borderRadius: 999,
            background: "rgba(56, 189, 248, 0.12)",
            border: `1px solid ${borderTitanium}`,
            backdropFilter: "blur(16px)",
          }}
        >
          <Sparkles size={18} color={cyanGlow} />
          <span
            style={{
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "0.12em",
              color: cyanGlow,
              textTransform: "uppercase",
            }}
          >
            KYUSTORE.COM • THE NEW STANDARD
          </span>
        </div>

        <h1
          style={{
            marginTop: 20,
            fontSize: 60,
            fontWeight: 900,
            letterSpacing: "-0.02em",
            textAlign: "center",
            textTransform: "uppercase",
            lineHeight: 1.05,
            color: "#FFFFFF",
          }}
        >
          Website Top-Up <span style={{ color: cyanGlow }}>Otomatis 24 Jam</span>
        </h1>
      </div>

      {/* 3. Central Stage: Floating Titanium Cards (Y: 480 - 1380 px) */}
      <div
        style={{
          position: "absolute",
          top: 480,
          left: 70,
          right: 70,
          display: "flex",
          flexDirection: "column",
          gap: 26,
        }}
      >
        {/* Main Titanium Stage Card */}
        <div
          style={{
            background: surfaceGlass,
            border: `1.5px solid ${borderTitanium}`,
            borderRadius: 32,
            padding: 32,
            boxShadow: "0 30px 60px rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(24px)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Top URL Bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              background: "rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 16,
              marginBottom: 24,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Lock size={18} color={emeraldGlow} />
              <span style={{ fontSize: 24, fontWeight: 800, color: "#FFFFFF" }}>
                kyustore.com
              </span>
            </div>
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: emeraldGlow,
                padding: "4px 12px",
                background: "rgba(52, 211, 153, 0.15)",
                borderRadius: 8,
              }}
            >
              SSL ENCRYPTED
            </span>
          </div>

          {/* QRIS Scanner Simulation Box */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 24,
              padding: 24,
              background: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 24,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Laser Line */}
            <div
              style={{
                position: "absolute",
                top: `${laserY}%`,
                left: 0,
                right: 0,
                height: 3,
                background: "linear-gradient(90deg, transparent, #34D399, transparent)",
                boxShadow: "0 0 15px #34D399",
                pointerEvents: "none",
              }}
            />

            <div
              style={{
                width: 90,
                height: 90,
                borderRadius: 20,
                background: "rgba(52, 211, 153, 0.12)",
                border: "1.5px solid rgba(52, 211, 153, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <QrCode size={52} color={emeraldGlow} />
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase" }}>
                Auto-Dispatch System
              </span>
              <span style={{ fontSize: 44, fontWeight: 900, color: "#FFFFFF", lineHeight: 1.1 }}>
                0.8 Detik Masuk
              </span>
              <span style={{ fontSize: 18, fontWeight: 600, color: emeraldGlow, marginTop: 4 }}>
                ✓ Verifikasi QRIS Instan Tanpa Delay
              </span>
            </div>
          </div>
        </div>

        {/* Feature Pill 1: Auto Nickname */}
        <div
          style={{
            background: surfaceGlass,
            border: `1.5px solid ${borderTitanium}`,
            borderRadius: 24,
            padding: "20px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 15px 35px rgba(0, 0, 0, 0.4)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <CheckCircle2 size={32} color={emeraldGlow} />
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#FFFFFF" }}>
                CEK NICKNAME OTOMATIS
              </div>
              <div style={{ fontSize: 18, color: "#94A3B8" }}>
                Ketik ID langsung muncul nama akunmu
              </div>
            </div>
          </div>
          <span
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: cyanGlow,
              padding: "6px 14px",
              background: "rgba(56, 189, 248, 0.12)",
              borderRadius: 10,
            }}
          >
            ANTI-SALAH ID
          </span>
        </div>

        {/* Feature Pill 2: Tanpa Password */}
        <div
          style={{
            background: surfaceGlass,
            border: `1.5px solid ${borderTitanium}`,
            borderRadius: 24,
            padding: "20px 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 15px 35px rgba(0, 0, 0, 0.4)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <ShieldCheck size={32} color={purpleGlow} />
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#FFFFFF" }}>
                100% AMAN & TANPA PASSWORD
              </div>
              <div style={{ fontSize: 18, color: "#94A3B8" }}>
                Cukup User ID, akun game tetap aman
              </div>
            </div>
          </div>
          <span
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: emeraldGlow,
              padding: "6px 14px",
              background: "rgba(52, 211, 153, 0.12)",
              borderRadius: 10,
            }}
          >
            100% LEGAL
          </span>
        </div>
      </div>

      {/* 4. Subtitle Safe Zone (Bottom: 380 - 440 px) */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 390,
            left: 80,
            right: 80,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              background: "rgba(11, 13, 20, 0.92)",
              border: `1px solid ${borderTitanium}`,
              borderRadius: 20,
              padding: "16px 28px",
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.7)",
              backdropFilter: "blur(20px)",
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 32, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.3 }}>
              {currentSub.text.split(currentSub.highlight).map((part, i, arr) => (
                <React.Fragment key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span
                      style={{
                        color: cyanGlow,
                        borderBottom: `2px solid ${cyanGlow}`,
                        paddingBottom: 2,
                      }}
                    >
                      {currentSub.highlight}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* 5. Master Voiceover Sync */}
      <Audio
        src={staticFile("kyustore_web_promo_enceladus.mp3")}
        volume={1.0}
      />
    </AbsoluteFill>
  );
};
